import React, { useEffect, useRef, useState } from "react";
import { Grid, Typography, TextField, Button, Box } from "@mui/material";

import Layout from "../../layout/Layout";

import { config, payslipAPI, holidaydetailsAPI, baseURL } from "../../config";
import axios from "axios";
import dayjs from "dayjs";
import moment from "moment";

// import companyLogo from "./../../assets/assets/../assets/images/Logo/logo.png"; // Keeping relative paths correct

import { useNavigate } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import { useParams } from "react-router-dom";

import { Snackbar, Alert } from "@mui/material";

import PaySlipPDF from "./pdf/PaySlipPdf";

function PaySlip() {
  const { employee_id, date } = useParams();

  const navigate = useNavigate();
  const ref = React.createRef();

  const [lstPayslip, setlstPayslip] = useState([]);
  const [initialPayslipList, setInitialPayslipList] = useState([]);
  const [workingData, setWorkingData] = useState(null);
  const [payslipMonthRange, setPayslipMonthRange] = useState("");

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const user = JSON.parse(localStorage.user);
  const payslipEditAccess = user.payslipEditAccess;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (workingData && lstPayslip.length > 0) {
      const updated = lstPayslip.map((emp) => {
        return {
          ...emp,
          leaveDays: emp.leaveDays ?? workingData.leaveDaysCount, // Default if null
        };
      });
      setlstPayslip(updated);
    }
  }, [workingData, lstPayslip.length]);

  useEffect(() => {
    const fetchWorkingDays = async () => {
      const result = await calculateWorkingDays(date);
      setWorkingData(result);

      const payslipRange = getPayslipMonthRange(date);
      setPayslipMonthRange(payslipRange);
    };

    if (date) fetchWorkingDays();
  }, [date]);

  const fetchData = async () => {
    // Check if payroll batch is frozen (Locked or Paid) for target month.
    const targetDate = dayjs(date).format("YYYY-MM");
    try {
      const companyRes = await axios({
        ...config,
        method: "POST",
        url: `${baseURL}/manage-companies`,
        data: JSON.stringify({ intType: 5 }),
      });
      const targetCompany = companyRes.data.result?.[0]?.id;
      
      if (targetCompany) {
        const batchRes = await axios({
          method: "GET",
          url: `${baseURL}/payroll/list/${targetCompany}`,
        });
        const batchList = batchRes.data.result || [];
        const activeBatch = batchList.find((b) => b.period_month_year === targetDate);
        
        if (!activeBatch || (activeBatch.status !== "Locked" && activeBatch.status !== "Paid")) {
          alert("Payslip cannot be viewed/generated because the payroll batch for this month is not approved or is still a draft.");
          navigate("/payslip-report");
          return;
        }
      }
    } catch (e) {
      console.error("Error verifying payroll status in PaySlip view", e);
    }

    const data = {
      intType: 2,
      intEmployeeID: employee_id,
      txtPayslipFromDate: dayjs(date).format("YYYY-MM-DD"),
    };

    config.method = "POST";
    config.data = JSON.stringify(data);
    config.url = payslipAPI;

    await axios(config)
      .then(function (response) {
        setlstPayslip(response.data.result);
        console.log("[DEBUG] Payslip Data:", response.data.result);
        setInitialPayslipList(response.data.result);
      })
      .catch(function (error) {
        console.error("[DEBUG] API Error:", error);
      });
  };

  function getPayslipMonthRange(payslipDate) {
    const toDate = moment(payslipDate).date(25);
    const fromDate = moment(payslipDate).subtract(1, "months").date(26);

    const fromFormatted = fromDate.format("DD MMMM");
    const toFormatted = toDate.format("DD MMMM YYYY");

    return `${fromFormatted} - ${toFormatted}`;
  }

  // count sundays
  function countSundays(fromDate, toDate) {
    let start = new Date(fromDate);
    const end = new Date(toDate);
    let sundays = 0;

    while (start <= end) {
      if (start.getDay() === 0) sundays++;
      start.setDate(start.getDate() + 1);
    }

    return sundays;
  }

  async function getHolidayCount(fromDate, toDate) {
    try {
      const response = await axios.post(holidaydetailsAPI, {
        txtHolidayDate: "",
        txtHolidayReason: "",
        txtFromDate: fromDate,
        txtToDate: toDate,
        intHolidayId: 0,
        intLimit: 0,
        intOffset: 0,
        intType: 6,
      });

      const holidayCount = response.data?.result?.[0]?.count || 0;

      return holidayCount;
    } catch (error) {
      console.error("Error fetching holidays:", error);
      return 0;
    }
  }

  async function calculateWorkingDays(date) {
    // From 26th of previous month
    const fromDate = moment(date)
      .subtract(1, "months")
      .date(26)
      .format("YYYY-MM-DD");

    // To 25th of current month
    const toDate = moment(date).date(25).format("YYYY-MM-DD");

    const start = moment(fromDate);
    const end = moment(toDate);

    const totalDays = end.diff(start, "days") + 1;

    const sundays = countSundays(fromDate, toDate);
    const holidays = await getHolidayCount(fromDate, toDate);

    const workingDays = totalDays - (sundays + holidays);

    const leaveDaysCount = sundays + holidays;

    return {
      fromDate,
      toDate,
      totalDays,
      sundays,
      holidays,
      leaveDaysCount,
      workingDays: workingDays < 0 ? 0 : workingDays,
    };
  }

  const handleFieldChange = (employeeId, field, value) => {
    if (value < 0) {
      return;
    }
    setlstPayslip((prev) =>
      prev.map((emp) =>
        emp.employee_id === employeeId
          ? {
              ...emp,
              [field]:
                field === "house_allowance" ||
                field === "medical_allowance" ||
                field === "med_sickLeave" ||
                field === "vehicle_allowance" ||
                field === "privilege_leave" ||
                field === "epf_fund" ||
                field === "professional_tax" ||
                field === "income_tax" ||
                field === "rd_esi" ||
                field === "employee_advance" ||
                field === "other_allowance" ||
                field === "salary_advance" ||
                field === "personal_advance" ||
                field === "ot_hrs" ||
                field === "dearness_allowance" ||
                field === "total_deductions" ||
                field === "net_pay" ||
                field === "gross_pay" ||
                field === "balance_advance" ||
                field === "petrol_conveyance" ||
                field === "manual_adjustments"
                  ? parseInt(value) || 0
                  : emp[field],
            }
          : emp
      )
    );
  };

  useEffect(() => {
    if (!lstPayslip || lstPayslip.length === 0) return;

    setlstPayslip((prev) =>
      prev.map((emp) => {
        const totalDays = workingData?.totalDays || 30;
        const leaveDays = emp.no_of_days_leave_taken || 0;
        const casualLeave = emp.casual_leave || 0;
        const unpaidLeave = Math.max(0, leaveDays - casualLeave);
        const paidDays = Math.max(0, totalDays - unpaidLeave);

        const earnedBasic = Math.round(parseFloat(emp.basic_pay || 0));
        const earnedHRA = Math.round(parseFloat(emp.house_allowance || 0));
        const earnedMedical = Math.round(parseFloat(emp.medical_allowance || 0));
        const earnedConveyance = Math.round(parseFloat(emp.vehicle_allowance || 0));
        const earnedOther = Math.round(parseFloat(emp.other_allowance || 0));
        const earnedPetrol = Math.round(parseFloat(emp.petrol_conveyance || 0));

        const totalDeductions =
          parseFloat(emp.professional_tax || 0) +
          parseFloat(emp.income_tax || 0) +
          parseFloat(emp.rd_esi || 0) +
          parseFloat(emp.salary_advance || 0) +
          parseFloat(emp.personal_advance || 0) +
          parseFloat(emp.epf_fund || 0) +
          parseFloat(emp.manual_adjustments || 0);

        const otSalary = Math.round(
          (parseFloat(emp.ot_hrs || 0)) *
            (parseFloat(emp.actual_pay || 0) / totalDays / 8)
        );

        const totalEarnings =
          earnedBasic +
          earnedHRA +
          earnedMedical +
          earnedConveyance +
          earnedOther +
          otSalary +
          earnedPetrol;

        const netPay = totalEarnings - totalDeductions;

        // Update only if needed
        if (
          emp.total_deductions !== totalDeductions ||
          emp.gross_pay !== totalEarnings ||
          emp.net_pay !== netPay
        ) {
          return {
            ...emp,
            total_deductions: totalDeductions,
            gross_pay: totalEarnings,
            net_pay: netPay,
          };
        }
        return emp;
      })
    );
  }, [lstPayslip, workingData]);

  const handleUpdateAllPayslips = async () => {
    try {
      const payload = lstPayslip.map((emp) => ({
        payslip_id: emp.id,
        professional_tax: emp.professional_tax,
        house_allowance: emp.house_allowance,
        medical_allowance: emp.medical_allowance,
        med_sickLeave: emp.med_sickLeave,
        vehicle_allowance: emp.vehicle_allowance,
        privilege_leave: emp.privilege_leave,
        epf_fund: emp.epf_fund,
        income_tax: emp.income_tax,
        rd_esi: emp.rd_esi,
        employee_advance: emp.employee_advance,
        other_allowance: emp.other_allowance,
        salary_advance: emp.salary_advance,
        personal_advance: emp.personal_advance,
        ot_hrs: emp.ot_hrs,
        dearness_allowance: emp.dearness_allowance,
        total_deductions: emp.total_deductions,
        net_pay: emp.net_pay,
        gross_pay: emp.gross_pay,
        balance_advance: emp.balance_advance,
        petrol_conveyance: emp.petrol_conveyance,
        manual_adjustments: emp.manual_adjustments,
      }));

      const response = await axios.post(payslipAPI, {
        intType: 99, // Custom type for bulk update
        data: payload,
      });

      if (response.data.success) {
        setToastMessage("Payslips updated successfully.");
        setToastOpen(true);
        await fetchData();
      } else {
        setToastMessage(response.data.message || "Update failed.");
        setToastOpen(true);
      }
    } catch (error) {
      console.error("Error updating all payslips:", error);
      setToastMessage(error.message || "Server error while updating.");
      setToastOpen(true);
    }
  };

  return (
    <Layout>
      <Grid container spacing={3}>
        <Grid
          textAlign="center"
          item
          className="form-lable"
          xs={12}
          lg={6}
          md={6}
        >
          <Typography className="page_title" textAlign="left">
            PaySlip
          </Typography>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/dashboard">
              Dashboard
            </Link>
            <Typography color="text.primary">payslip</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid
          textAlign="right"
          item
          className="form-lable"
          xs={12}
          lg={6}
          md={6}
        >
          <LoadingButton
            size="medium"
            variant="contained"
            color="primary"
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIosIcon />}
          >
            Back
          </LoadingButton>
        </Grid>
      </Grid>
      <div className="empty-space-40"></div>
      <Grid container spacing={2} className="form_design">
        <Grid item xs={12} md={12} lg={12}>
          {lstPayslip.map((row, key) => (
            <>
              <Grid
                className="element-to-print"
                paddingLeft={`55px`}
                paddingTop={`55px`}
                paddingRight={`55px`}
                ref={ref}
              >
                <Grid className="page" width={`100%`}>
                  <table>
                    <tr>
                      <td colSpan={5}>
                        <table>
                          <tr>
                            <td colSpan={3} width={`75%`} className="no-border">
                              <table>
                                <tr>
                                  <td width={`35%`} className="no-border">
                                    <Typography className={`td text-bold`}>
                                      Employee Name
                                    </Typography>
                                  </td>
                                  <td width={`65%`} className="no-border">
                                    <Typography className={`td`}>
                                      {row.employee_name}
                                    </Typography>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="no-border">
                                    <Typography className={`td text-bold`}>
                                      Employee ID
                                    </Typography>
                                  </td>
                                  <td className="no-border">
                                    <Typography className={`td `}>
                                      {row.employee_no}
                                    </Typography>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="no-border">
                                    <Typography className={`td text-bold`}>
                                      Designation
                                    </Typography>
                                  </td>
                                  <td className="no-border">
                                    <Typography className={`td`}>
                                      {row.position}
                                    </Typography>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="no-border">
                                    <Typography className={`td text-bold`}>
                                      Company Name
                                    </Typography>
                                  </td>
                                  <td className="no-border">
                                    <Typography className={`td`}>
                                      {row.company_name}
                                    </Typography>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="no-border">
                                    <Typography className={`td text-bold`}>
                                      Department
                                    </Typography>
                                  </td>
                                  <td className="no-border">
                                    <Typography className={`td`}>
                                      {row.department_name}
                                    </Typography>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="no-border">
                                    <Typography className={`td text-bold`}>
                                      Bank Name
                                    </Typography>
                                  </td>
                                  <td className="no-border">
                                    <Typography className={`td`}>
                                      {row.bank_name || "Not Available"}
                                    </Typography>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="no-border">
                                    <Typography className={`td text-bold`}>
                                      A/c. Number
                                    </Typography>
                                  </td>
                                  <td className="no-border">
                                    <Typography className={`td`}>
                                      {row.account_number || "Not Available"}
                                    </Typography>
                                  </td>
                                </tr>
                              </table>
                            </td>

                            <tr>
                              <td width={`10%`} className="no-border">
                                <img
                                  src={row.company_logo || "Not Available"}
                                  alt="KG"
                                  width={`200px`}
                                  height={`auto`}
                                />
                              </td>
                            </tr>

                            <tr>
                              <td width={`10%`} className="no-border">
                                <Typography className={`td`}>
                                  {row.address}
                                </Typography>
                              </td>
                            </tr>

                            <tr>
                              <td width={`35%`} className="no-border">
                                <Typography className={`td text-bold`}>
                                  Payslip: {payslipMonthRange}
                                </Typography>
                              </td>
                            </tr>
                          </tr>
                        </table>
                      </td>
                    </tr>

                    <tr style={{ backgroundColor: "#d9d9d9" }}>
                      <td width={`25%`}>
                        <Typography className={`td text-center text-bold`}>
                          Emoluments{" "}
                        </Typography>
                      </td>
                      <td width={`12%`}>
                        <Typography className={`td text-center text-bold`}>
                          Actual Rs.{" "}
                        </Typography>
                      </td>
                      <td width={`13%`}>
                        <Typography className={`td text-center text-bold`}>
                          Earned Rs.{" "}
                        </Typography>
                      </td>
                      <td width={`35%`}>
                        <Typography className={`td text-center text-bold`}>
                          Deductions{" "}
                        </Typography>
                      </td>
                      <td width={`15%`}>
                        <Typography className={`td text-center text-bold`}>
                          Amount Rs.{" "}
                        </Typography>
                      </td>
                    </tr>
                    <tr style={{ backgroundColor: "#d9d9d9" }}>
                      <td>
                        <Typography className={`td`}>Actual Salary</Typography>
                      </td>
                      <td>
                        <Typography className={`td text-right`}>
                          {row.actual_pay.toLocaleString()}
                        </Typography>
                      </td>
                      <td>
                        <Typography className={`td text-right`}>
                          {Math.round(
                            (row.actual_pay *
                              Math.max(
                                0,
                                (workingData?.totalDays || 30) -
                                  Math.max(
                                    0,
                                    (row.no_of_days_leave_taken || 0) -
                                      (row.casual_leave || 0)
                                  )
                              )) /
                              (workingData?.totalDays || 30)
                          ).toLocaleString()}
                        </Typography>
                      </td>
                      <td>
                        <Typography className={`td`}>Total Days</Typography>
                      </td>
                      <td>
                        <Typography className={`td text-right`}>
                          {workingData?.totalDays}
                        </Typography>
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <Typography className={`td`}>Basic Salary</Typography>
                      </td>
                      <td>
                        <Typography className={`td text-right`}>
                          {row.basic_pay.toLocaleString()}
                        </Typography>
                      </td>
                      <td>
                        <Typography className={`td text-right`}>
                          {Math.round(
                            (row.basic_pay *
                              Math.max(
                                0,
                                (workingData?.totalDays || 30) -
                                  Math.max(
                                    0,
                                    (row.no_of_days_leave_taken || 0) -
                                      (row.casual_leave || 0)
                                  )
                              )) /
                              (workingData?.totalDays || 30)
                          ).toLocaleString()}
                        </Typography>
                      </td>
                      <td>
                        <Typography className={`td`}>Full Days</Typography>
                      </td>
                      <td>
                        <Typography className={`td text-right`}>
                          {(row.no_of_days_working !== null && row.no_of_days_working !== undefined ? row.no_of_days_working : row.full_days || 0).toLocaleString()}
                        </Typography>
                      </td>
                    </tr>

                    {lstPayslip.map((employee) => {
                      const totalDays = workingData?.totalDays || 30;
                      const leaveDays = employee.no_of_days_leave_taken || 0;
                      const casualLeave = employee.casual_leave || 0;
                      const unpaidLeave = Math.max(0, leaveDays - casualLeave);
                      const paidDays = Math.max(0, totalDays - unpaidLeave);

                      const earnedHRA = Math.round(
                        ((employee.house_allowance || 0) * paidDays) / totalDays
                      );
                      const earnedMedical = Math.round(
                        ((employee.medical_allowance || 0) * paidDays) /
                          totalDays
                      );
                      const earnedConveyance = Math.round(
                        ((employee.vehicle_allowance || 0) * paidDays) /
                          totalDays
                      );
                      const earnedOther = Math.round(
                        ((employee.other_allowance || 0) * paidDays) / totalDays
                      );
                      const earnedPetrol = Math.round(
                        ((employee.petrol_conveyance || 0) * paidDays) /
                          totalDays
                      );
                      const otSalary = Math.round(
                        (employee.ot_hrs || 0) *
                          ((employee.actual_pay || 0) / totalDays / 8)
                      );

                      return (
                        <React.Fragment key={employee.employee_id}>
                          <tr key={employee.employee_id + "_row_hra"}>
                            <td>
                              <Typography className={`td`}>HRA</Typography>
                            </td>
                            <td>
                              <Box display="flex" alignItems="center">
                                <TextField
                                  value={employee.house_allowance || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      employee.employee_id,
                                      "house_allowance",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      width: "100px",
                                      padding: "5px",
                                      textAlign: "right",
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                />
                              </Box>
                            </td>
                            <td>
                              <Typography className={`td text-right`}>
                                {earnedHRA.toLocaleString()}
                              </Typography>
                            </td>
                            <td>
                              <Typography className={`td`}>
                                Leave Days
                              </Typography>
                            </td>
                            <td>
                              <Typography className={`td text-right`}>
                                {(employee.no_of_days_leave_taken !== null && employee.no_of_days_leave_taken !== undefined ? employee.no_of_days_leave_taken : employee.leave_days || 0)}
                              </Typography>
                            </td>
                          </tr>

                          <tr key={employee.employee_id + "_row_med"}>
                            <td>
                              <Typography className={`td`}>
                                Conveyance Allowance
                              </Typography>
                            </td>
                            <td>
                              <Box display="flex" alignItems="center">
                                <TextField
                                  value={employee.medical_allowance || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      employee.employee_id,
                                      "medical_allowance",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      width: "100px",
                                      padding: "5px",
                                      textAlign: "right",
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                />
                              </Box>
                            </td>
                            <td>
                              <Typography className={`td text-right`}>
                                {earnedMedical.toLocaleString()}
                              </Typography>
                            </td>
                            <td>
                              <Typography className={`td`}>
                                Casual Leave
                              </Typography>
                            </td>
                            <td>
                              <Typography className={`td text-right`}>
                                {employee.casual_leave || 0}
                              </Typography>
                            </td>
                          </tr>

                          <tr key={employee.employee_id + "_row_conv"}>
                            <td>
                              <Typography className={`td`}>
                                Special Allowance
                              </Typography>
                            </td>
                            <td>
                              <Box display="flex" alignItems="center">
                                <TextField
                                  value={employee.vehicle_allowance || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      employee.employee_id,
                                      "vehicle_allowance",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      width: "100px",
                                      padding: "5px",
                                      textAlign: "right",
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                />
                              </Box>
                            </td>
                            <td>
                              <Typography className={`td text-right`}>
                                {earnedConveyance.toLocaleString()}
                              </Typography>
                            </td>
                            <td>
                              <Typography className={`td`}>OT Hours</Typography>
                            </td>
                            <td>
                              <Box display="flex" alignItems="center">
                                <TextField
                                  value={employee.ot_hrs || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      employee.employee_id,
                                      "ot_hrs",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      width: "150px",
                                      padding: "5px",
                                      textAlign: "right",
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                />
                              </Box>
                            </td>
                          </tr>

                          <tr key={employee.employee_id + "_row_other"}>
                            <td>
                              <Typography className={`td`}>
                                Other Allowance
                              </Typography>
                            </td>
                            <td>
                              <Box display="flex" alignItems="center">
                                <TextField
                                  value={employee.other_allowance || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      employee.employee_id,
                                      "other_allowance",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      width: "100px",
                                      padding: "5px",
                                      textAlign: "right",
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                />
                              </Box>
                            </td>
                            <td>
                              <Typography className={`td text-right`}>
                                {earnedOther.toLocaleString()}
                              </Typography>
                            </td>
                            <td>
                              <Typography className={`td`}>
                                Salary Advance (Same Month)
                              </Typography>
                            </td>
                            <td>
                              <Box display="flex" alignItems="center">
                                <TextField
                                  value={employee.salary_advance || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      employee.employee_id,
                                      "salary_advance",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      width: "150px",
                                      padding: "5px",
                                      textAlign: "right",
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                />
                              </Box>
                            </td>
                          </tr>

                          <tr key={employee.employee_id + "_row_otsal"}>
                            <td>
                              <Typography className={`td`}>
                                OT SALARY
                              </Typography>
                            </td>
                            <td>
                              <Typography className={`td text-right`}>
                                0.00
                              </Typography>
                            </td>
                            <td>
                              <Typography className={`td text-right`}>
                                {otSalary.toLocaleString()}
                              </Typography>
                            </td>
                            <td>
                              <Typography className={`td`}>
                                Personal Advance (Monthly Wise)
                              </Typography>
                            </td>
                            <td>
                              <Box display="flex" alignItems="center">
                                <TextField
                                  value={employee.personal_advance || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      employee.employee_id,
                                      "personal_advance",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      width: "150px",
                                      padding: "5px",
                                      textAlign: "right",
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                />
                              </Box>
                            </td>
                          </tr>

                          <tr key={employee.employee_id + "_row_petrol"}>
                            <td>
                              <Typography className={`td`}>
                                Petrol Conveyance
                              </Typography>
                            </td>
                            <td>
                              <Box display="flex" alignItems="center">
                                <TextField
                                  value={employee.petrol_conveyance || 0}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      employee.employee_id,
                                      "petrol_conveyance",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      width: "100px",
                                      padding: "5px",
                                      textAlign: "right",
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                />
                              </Box>
                            </td>
                            <td>
                              <Typography className={`td text-right`}>
                                {earnedPetrol.toLocaleString()}
                              </Typography>
                            </td>
                            <td>
                              <Typography className={`td`}>
                                Employee Provident Fund (EPF)
                              </Typography>
                            </td>
                            <td>
                              <Box display="flex" alignItems="center">
                                <TextField
                                  value={employee.epf_fund || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      employee.employee_id,
                                      "epf_fund",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      width: "150px",
                                      padding: "5px",
                                      textAlign: "right",
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                />
                              </Box>
                            </td>
                          </tr>

                          <tr key={employee.employee_id + "_row_pt"}>
                            <td style={{ backgroundColor: "#ffd966" }}>
                              <Typography className={`td text-bold`}>
                                GROSS SALARY
                              </Typography>
                            </td>
                            <td style={{ backgroundColor: "#ffd966" }}>
                              <Typography className={`td text-right text-bold`}>
                                {employee.actual_pay?.toLocaleString() || 0}
                              </Typography>
                            </td>
                            <td style={{ backgroundColor: "#ffd966" }}>
                              <Typography className={`td text-right text-bold`}>
                                {employee.gross_pay?.toLocaleString() || 0}
                              </Typography>
                            </td>
                            <td>
                              <Typography className={`td`}>
                                Professional Tax (PT) (200 TO 2000)
                              </Typography>
                            </td>
                            <td>
                              <Box display="flex" alignItems="center">
                                <TextField
                                  value={employee.professional_tax || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      employee.employee_id,
                                      "professional_tax",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      width: "150px",
                                      padding: "5px",
                                      textAlign: "right",
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                />
                              </Box>
                            </td>
                          </tr>

                          <tr key={employee.employee_id + "_row_tds"}>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td>
                              <Typography className={`td`}>
                                Tax Deducted at Source (TDS)
                              </Typography>
                            </td>
                            <td>
                              <Box display="flex" alignItems="center">
                                <TextField
                                  value={employee.income_tax || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      employee.employee_id,
                                      "income_tax",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      width: "150px",
                                      padding: "5px",
                                      textAlign: "right",
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                />
                              </Box>
                            </td>
                          </tr>

                          <tr key={employee.employee_id + "_row_esi"}>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td>
                              <Typography className={`td`}>ESI</Typography>
                            </td>
                            <td>
                              <Box display="flex" alignItems="center">
                                <TextField
                                  value={employee.rd_esi || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      employee.employee_id,
                                      "rd_esi",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      width: "150px",
                                      padding: "5px",
                                      textAlign: "right",
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                />
                              </Box>
                            </td>
                          </tr>

                          <tr key={employee.employee_id + "_row_manual_adj"}>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td>
                              <Typography className={`td`}>Other Deductions/Fine</Typography>
                            </td>
                            <td>
                              <Box display="flex" alignItems="center">
                                <TextField
                                  value={employee.manual_adjustments || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      employee.employee_id,
                                      "manual_adjustments",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    style: {
                                      width: "150px",
                                      padding: "5px",
                                      textAlign: "right",
                                    },
                                  }}
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                />
                              </Box>
                            </td>
                          </tr>

                          <tr key={employee.employee_id + "_row_ded"}>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td style={{ backgroundColor: "#fce4d6" }}>
                              <Typography className="td text-bold">
                                Total Deduction
                              </Typography>
                            </td>
                            <td style={{ backgroundColor: "#fce4d6" }}>
                              <Typography className={`td text-right text-bold`}>
                                {employee.total_deductions}
                              </Typography>
                            </td>
                          </tr>

                          <tr key={employee.employee_id + "_row_net"}>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td style={{ backgroundColor: "#e2efda" }}>
                              <Typography className={`td text-bold`}>
                                Net Payable / Total Earnings
                              </Typography>
                            </td>
                            <td style={{ backgroundColor: "#e2efda" }}>
                              <Typography className={`td text-right text-bold`}>
                                {employee.net_pay}
                              </Typography>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </table>
                </Grid>
              </Grid>

              {/* <Grid
                container
                spacing={2}
                justifyContent={`flex-end`}
                sx={{ my: 2 }}
              >
                <Pdf
                  targetRef={ref}
                  filename={`Pay Slip-${moment()
                    .locale('en-in')
                    .format('LLL')} .pdf`}
                  y={7}
                  x={4}
                  scale={0.7}
                >
                  {({ toPdf }) => (
                    <Button
                      className='button'
                      onClick={toPdf}
                      variant='contained'
                    >
                      <PictureAsPdfIcon />
                      Generate Pdf
                    </Button>
                  )}
                </Pdf>
              </Grid> */}

              <Grid
                container
                spacing={2}
                justifyContent={`flex-end`}
                sx={{ my: 2 }}
              >
                {/* {Boolean(payslipEditAccess) && ( */}
                  <Grid item sx={{ mr: 4.2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleUpdateAllPayslips}
                      sx={{
                        backgroundColor: "#4CAF50",
                        fontWeight: "bold",
                        transition:
                          "background-color 0.3s ease, box-shadow 0.3s ease",
                        "&:hover": {
                          backgroundColor: "#FFF",
                          color: "#4CAF50",
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                        },
                      }}
                    >
                      Update Payslip
                    </Button>
                  </Grid>
                {/* // )} */}
                <Grid item sx={{ mr: 6.2 }}>
                  <PaySlipPDF
                    paySlip={lstPayslip[0]}
                    company_logo={lstPayslip[0]?.company_logo}
                    workingData={workingData}
                    payslipMonthRange={payslipMonthRange}
                  />
                </Grid>
              </Grid>
            </>
          ))}
        </Grid>
      </Grid>
    </Layout>
  );
}
export default PaySlip;
